import type {
  MaterializedMenuProduct,
  QualifyingStatusProduct,
} from '@iheartjane/data-access';
import type { AppMode, PriceId } from '@iheartjane/types';
import find from 'lodash-es/find';

// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, StoreSpecial } from '@jane/shared/models';
import type { StoreSpecial as ZStoreSpecial } from '@jane/shared/types';

export const QUALIFIED_GROUP_TYPE_MAP = {
  student: 'Student',
  veteran: 'Veteran',
  medical: 'Medical',
  senior: 'Senior',
  pediatric: 'Pediatric',
  ssi: 'SSI',
  snap: 'SNAP',
  pace: 'PACE',
  pacenet: 'PACENET',
  wic: 'WIC',
  chip: 'CHIP',
  industry: 'Industry',
  medicare: 'Medicare',
  medicaid: 'Medicaid',
  member: 'Member',
  locals: 'Locals',
  'PA Gov': 'PA Gov',
};

export const doesSpecialApply = (
  appMode: AppMode,
  special: StoreSpecial | ZStoreSpecial,
  menuProduct: MenuProduct | MaterializedMenuProduct,
  weight?: PriceId
) => {
  // safeguard against group special
  if (special.special_type !== 'product') return false;

  const notKiosk = appMode !== 'kiosk' || special.reservation_modes?.kiosk;

  if (!weight) {
    // whitelist special based on the BE endpoint as the source of truth
    return menuProduct.special_id === special.id && notKiosk;
  }

  const specialWeights = special?.rules?.includes?.[0]?.weights ?? [];
  return (
    menuProduct.applicable_special_ids?.includes(special.id) &&
    (specialWeights.length === 0 || specialWeights.includes(weight))
  );
};

export const findSpecialForProductPriceId = (
  menuProduct: MenuProduct,
  specials: StoreSpecial[] = [],
  weight: PriceId
) => {
  if (!specials.length) return undefined;

  return specials.find((special: StoreSpecial) => {
    if (special.special_type !== 'product') return false;

    const specialForPriceId = menuProduct[`special_price_${weight}`];

    if (!specialForPriceId) return false;

    return special.id === specialForPriceId.special_id;
  });
};

// Annotate discounted products that are also qualifiers so we can skip rendering them
export const getDiscountedProductsForBundle = (
  discountedProducts: QualifyingStatusProduct[],
  qualifiedProducts: QualifyingStatusProduct[]
) => {
  return discountedProducts.map((product) => {
    const qualifiedProduct = find(qualifiedProducts, {
      id: product.id,
      price_id: product.price_id,
    });

    if (qualifiedProduct) {
      return {
        ...product,
        count: qualifiedProduct.count,
        discountableIsAlsoQualifier: true,
      };
    }

    return product;
  });
};
